<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Estadísticas del Ciclo de Vida de Albaranes</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <ol class="breadcrumb breadcrumb-links breadcrumb-dark">
              <li class="breadcrumb-item">
                <router-link to="/dashboard">
                  <i class="fas fa-home"></i>
                </router-link>
              </li>
              <li class="breadcrumb-item">
                <router-link to="/albaran-management/list-albaranes">Albaranes</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">Estadísticas</li>
            </ol>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button
            size="sm"
            type="neutral"
            @click="refreshStats"
            :loading="loading"
          >
            <i class="fas fa-sync mr-2"></i>
            Actualizar
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- KPIs Principales -->
      <div class="row">
        <div class="col-xl-3 col-md-6">
          <stats-card
            title="Total Albaranes"
            type="gradient-primary"
            :sub-title="stats.total?.toString() || '0'"
            icon="ni ni-collection"
          >
            <template slot="footer">
              <span class="text-success mr-2">{{ stats.porcentajeCompletados }}%</span>
              <span class="text-nowrap">completados</span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            title="Clones Activos"
            type="gradient-purple"
            :sub-title="stats.totalClones?.toString() || '0'"
            icon="ni ni-single-copy-04"
          >
            <template slot="footer">
              <span class="text-purple mr-2">{{ stats.porcentajeClones }}%</span>
              <span class="text-nowrap">del total</span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            title="Sin Tarea"
            type="gradient-danger"
            :sub-title="stats.totalSinTarea?.toString() || '0'"
            icon="ni ni-circle-08"
          >
            <template slot="footer">
              <span class="text-danger mr-2">{{ stats.porcentajeSinTarea }}%</span>
              <span class="text-nowrap">del total</span>
            </template>
          </stats-card>
        </div>
      </div>

      <!-- Gráficos y Análisis -->
      <div class="row">
        <div class="col-lg-8">
          <card>
            <template v-slot:header>
              <h3 class="mb-0">Evolución del Ciclo de Vida</h3>
            </template>
            <div class="chart-container" style="position: relative; height: 300px;">
              <canvas ref="lifecycleChart"></canvas>
            </div>
          </card>
        </div>
        <div class="col-lg-4">
          <card>
            <template v-slot:header>
              <h3 class="mb-0">Distribución por Estado</h3>
            </template>
            <div class="chart-container" style="position: relative; height: 300px;">
              <canvas ref="statusChart"></canvas>
            </div>
          </card>
        </div>
      </div>


      <!-- Tabla de Clones Recientes -->
      <div class="row">
        <div class="col-12">
          <card>
            <template v-slot:header>
              <h3 class="mb-0">Clones Recientes</h3>
              <p class="text-muted mb-0">Últimos albaranes clonados por materiales pendientes</p>
            </template>
            <div class="table-responsive">
              <table class="table align-items-center table-flush">
                <thead class="thead-light">
                  <tr>
                    <th scope="col">Número Clon</th>
                    <th scope="col">Albarán Original</th>
                    <th scope="col">Fecha Clonación</th>
                    <th scope="col">Materiales Pendientes</th>
                    <th scope="col">Tarea Asociada</th>
                    <th scope="col">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="clon in clonesRecientes" :key="clon.id">
                    <td>{{ clon.numero }}</td>
                    <td>{{ clon.albaran_original_id }}</td>
                    <td>{{ formatDate(clon.createdAt) }}</td>
                    <td>
                      <span class="badge badge-info text-white">
                        {{ clon.materialesPendientes }} materiales
                      </span>
                    </td>
                    <td>
                      <span v-if="clon.tarea_keikaku_id" class="badge badge-success text-white">
                        {{ clon.tarea_keikaku_id }}
                      </span>
                      <span v-else class="badge badge-warning text-white">
                        Sin tarea
                      </span>
                    </td>
                    <td>
                      <base-button
                        size="sm"
                        type="primary"
                        @click="verAlbaran(clon.id)"
                      >
                        <i class="fas fa-eye mr-1"></i>
                        Ver
                      </base-button>
                    </td>
                  </tr>
                  <tr v-if="clonesRecientes.length === 0">
                    <td colspan="6" class="text-center text-muted">
                      No hay clones recientes
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import axios from 'axios';
import Chart from 'chart.js';

moment.locale("es");

export default {
  name: "albaran-stats",
  data() {
    return {
      loading: false,
      stats: {},
      apiUrl: process.env.VUE_APP_API_BASE_URL || 'https://misepis-api.delfromsoftware.com/api',
      clonesRecientes: [],
      lifecycleChart: null,
      statusChart: null
    };
  },
  computed: {
    chartData() {
      return {
        labels: ['Pendiente Asignar', 'Asignado', 'Cerrado', 'Clonado'],
        datasets: [{
          label: 'Cantidad',
          data: [
            this.stats.totalPendientesAsignar || 0,
            this.stats.totalAsignados || 0,
            this.stats.totalCerrados || 0,
            this.stats.totalClones || 0
          ],
          backgroundColor: [
            '#f5365c',
            '#fb6340',
            '#11cdef',
            '#8965e0'
          ]
        }]
      };
    }
  },
  methods: {
    calculateStats(albaranes) {
      const total = albaranes.length;
      const completados = albaranes.filter(a => a.attributes?.estado === 'cerrado' || a.estado === 'cerrado').length;
      const clones = albaranes.filter(a => a.attributes?.es_clon === true || a.es_clon === true).length;
      const sinTarea = albaranes.filter(a => a.attributes?.estado === 'cerrado_sin_tarea' || a.estado === 'cerrado_sin_tarea').length;

      return {
        total,
        totalCompletados: completados,
        totalClones: clones,
        totalSinTarea: sinTarea,
        porcentajeCompletados: total > 0 ? Math.round((completados / total) * 100) : 0,
        porcentajeClones: total > 0 ? Math.round((clones / total) * 100) : 0,
        porcentajeSinTarea: total > 0 ? Math.round((sinTarea / total) * 100) : 0
      };
    },

    async loadStats() {
      try {
        this.loading = true;
        
        // Cargar estadísticas básicas desde el endpoint existente
        const statsResponse = await axios.get(
          `${this.apiUrl}/albaran-activos?populate=*`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Procesar los datos para generar estadísticas
        const albaranes = statsResponse.data.data || [];
        this.stats = this.calculateStats(albaranes);

        // Cargar clones recientes (últimos 10)
        const clonesResponse = await axios.get(
          `${this.apiUrl}/albaran-activos?filters[es_clon][$eq]=true&sort=createdAt:desc&pagination[pageSize]=10&populate=*`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        this.clonesRecientes = (clonesResponse.data.data || []).map(clon => ({
          id: clon.id,
          numero: clon.attributes?.numero || clon.numero,
          titulo: clon.attributes?.titulo || clon.titulo,
          fecha_creacion: clon.attributes?.createdAt || clon.createdAt,
          albaran_original: clon.attributes?.albaran_original_id || clon.albaran_original_id
        }));

        this.updateCharts();

      } catch (error) {
        console.error("Error cargando estadísticas:", error);
        this.$notify({
          type: "danger",
          message: "Error al cargar las estadísticas: " + (error.response?.data?.error?.message || "Error desconocido")
        });
      } finally {
        this.loading = false;
      }
    },

    updateCharts() {
      this.$nextTick(() => {
        // Gráfico de distribución por estado
        if (this.statusChart) {
          this.statusChart.destroy();
        }
        
        const statusCtx = this.$refs.statusChart;
        if (statusCtx) {
          this.statusChart = new Chart(statusCtx, {
            type: 'doughnut',
            data: this.chartData,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom'
                }
              }
            }
          });
        }

        // Gráfico de evolución (simulado por ahora)
        if (this.lifecycleChart) {
          this.lifecycleChart.destroy();
        }
        
        const lifecycleCtx = this.$refs.lifecycleChart;
        if (lifecycleCtx) {
          this.lifecycleChart = new Chart(lifecycleCtx, {
            type: 'line',
            data: {
              labels: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
              datasets: [{
                label: 'Albaranes Creados',
                data: [12, 19, 3, 5, 2, 3, 7],
                borderColor: '#11cdef',
                backgroundColor: 'rgba(17, 205, 239, 0.1)',
                tension: 0.4
              }, {
                label: 'Albaranes Cerrados',
                data: [8, 15, 2, 4, 1, 2, 5],
                borderColor: '#2dce89',
                backgroundColor: 'rgba(45, 206, 137, 0.1)',
                tension: 0.4
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'top'
                }
              },
              scales: {
                y: {
                  beginAtZero: true
                }
              }
            }
          });
        }
      });
    },

    refreshStats() {
      this.loadStats();
    },

    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "-";
    },

    verAlbaran(id) {
      this.$router.push({
        name: "View Albaran",
        params: { id }
      });
    }
  },
  async created() {
    await this.loadStats();
  },
  beforeDestroy() {
    if (this.lifecycleChart) {
      this.lifecycleChart.destroy();
    }
    if (this.statusChart) {
      this.statusChart.destroy();
    }
  }
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 300px;
}
</style>
